// Which execution-table columns still fit, given the width of `.tablewrap`.
//
// Account is the one flexible column: it absorbs slack when there is room, which is why it
// carries no `<col>` width. That is right in the surplus case and was incomplete in the
// deficit case — every other column kept its pixels, so the name was the first thing to
// vanish. This module is the deficit case.
//
// The pattern, for the next table that copies it:
//
//   1. Give the identity column a `minWidth` floor, not a fixed width.
//   2. Give every other column a `<col>` width, as before — without that, the layout
//      still shifts when a filter changes which rows are on screen.
//   3. Drop the least useful remaining column until the floor plus the surviving widths
//      fit the *container*. Observe the wrap with ResizeObserver, not the viewport: a
//      browser-extension panel narrows the page without a matchMedia breakpoint firing.
//   4. Whatever is left scrolls inside `.tablewrap`. Do not add a second overflow.
//
// The drop order is a proposal from GRO-139, least valuable first. Omair works this table
// every day and has not confirmed it. Account and rank never drop. Vikram, 29 Sep (GRO-159):
// Worked is gone from the board. Vikram, 20 Oct: Usage must not be cut before Funding, so Funding
// goes first, then Our VC, then the AE and Last touch; Usage (credits) is among the
// last to go, ahead of only Why now. Fit and Engagement never drop. The keys are the v2 board's
// (`boardV2Csv.ts`); the old ones matched none of them.

export interface ColumnFit {
  key: string;
  width?: string;
  minWidth?: number;
}

export const ACCOUNT_FLOOR_PX = 180;

// The Pin / Hide column after the data columns (`td.prefcell` in table.css). It is always drawn,
// so the fit has to leave room for it, or a narrow board pushes the buttons off the right edge.
export const PREF_COLUMN_PX = 88;

export const COLUMN_DROP_ORDER = [
  'tier',
  'employee_count',
  'funding',
  'bucket',
  'relationship',
  'account_owner',
  'reachable',
  'last_out',
  'credits_30',
  'why_now',
] as const;

// Fit and Engagement are the scores the board exists to show, so they stay with the account and rank.
// The High priority flag (26px) stays too: it is how leadership's ask reaches the strategist.
const NEVER_DROP = new Set(['account', 'rank', 'fit_score', 'engagement_score', 'high_priority']);

export function columnPixels(column: ColumnFit): number {
  const fromWidth = Number.parseInt(column.width ?? '', 10);
  const parsed = Number.isFinite(fromWidth) ? fromWidth : 0;
  return Math.max(parsed, column.minWidth ?? 0);
}

export function tableMinWidth(columns: ColumnFit[]): number {
  let total = 0;
  for (const column of columns) total += columnPixels(column);
  return total;
}

export function columnsThatFit<T extends ColumnFit>(columns: T[], containerWidth: number): T[] {
  if (!Number.isFinite(containerWidth) || containerWidth <= 0) return columns;

  const present = new Set(columns.map(column => column.key));
  const hidden = new Set<string>();

  const needed = () => {
    let total = 0;
    for (const column of columns) {
      if (!hidden.has(column.key)) total += columnPixels(column);
    }
    return total;
  };

  for (const key of COLUMN_DROP_ORDER) {
    if (needed() <= containerWidth) break;
    if (!present.has(key) || NEVER_DROP.has(key)) continue;
    hidden.add(key);
  }

  if (hidden.size === 0) return columns;
  return columns.filter(column => !hidden.has(column.key));
}
